import { FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Typography } from '@mui/material';
import { Grid } from '@mui/system';
import moment from 'moment';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import { largeCurrencyFormat } from 'common/formats/formats';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport/constants';
import { CustomContainer, InlineMessageBox } from 'components';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import { useLayoutContext } from 'context/LayoutContext';
import { ExportSignalStatus, useExportSignal } from 'features/pdfExport';
import { valuationTabId } from 'pages/ValuationsAllocation/common/utils/valuationTabId';
import { toOPMDate } from 'pages/ValuationsAllocation/components/ShowOPMCalculationsLink/readOPMCalculatorInputs';
import { useApproachQueryParam } from 'pages/ValuationsAllocation/hooks/useApproachQueryParam';
import { useOPMCalculatorUrlState } from 'pages/ValuationsAllocation/hooks/useOPMCalculatorUrlState';
import { getOPMCalculatorPanelId } from 'pages/ValuationsAllocation/util/getOPMCalculatorPanelId';
import { VALUATION_APPROACHES_TEST_IDS } from 'pages/ValuationsAllocation/ValuationApproaches.testIds';
import { useGetRiskFreeRate } from 'services/hooks/allocation';
import { formatNumbers, getNumberValue, getStringValue, ISO_DATE_FORMAT } from 'utilities';
import {
  createAllocationConfig,
  createInputsConfig,
  createLadderConfig,
  createSecurityConfig,
} from './config';
import {
  CAP_TABLE_SELECTION_ALIAS,
  EQUITY_VALUE_ALIAS,
  getOPMCalculatorPrefilledFromCaption,
  MATURITY_ALIAS,
  OPM_CALCULATOR_ERROR_TITLE,
  OPM_CALCULATOR_MATURITY_DECIMAL_PLACES,
  OPM_CALCULATOR_PLACEHOLDER_TAGLINE,
  OPM_CALCULATOR_PREFILLED_FROM_TEST_ID,
  OPM_DATE_ALIAS,
  VOLATILITY_ALIAS,
} from './constants';
import { useOPMCalculatorResults } from './hooks';
import { CapTableOption, OPMCalculatorInputs, OPMCalculatorProps, OPMCalculatorSpreadsheetConfig } from './types';
import ValuationContext from '../../ValuationContext';

/**
 * Module-level so the fallback keeps one identity. `capTableList` is absent from the store's
 * initial state and is reset to `null` when a valuation unmounts, so an inline `[]` fallback
 * produced a fresh array on every render -- which invalidated the inputs-sheet memo, which
 * rebuilt the sheet list, which re-rendered, until React aborted the page with "Maximum update
 * depth exceeded".
 */
const EMPTY_CAP_TABLE_LIST: CapTableOption[] = [];

/**
 * `OPMCalculator.maturity` holds two decimals and the results serializer mirrors that, so a
 * third decimal comes back as a 400 instead of a rounded value. Round at both entry points --
 * the cell edit and a deep link's `opm-maturity` -- rather than at the request, so the number
 * saved on the row and the number shown in the sheet are the one that was priced.
 */
const roundMaturity = (maturity?: number) =>
  (maturity === undefined
    ? undefined
    : Number(maturity.toFixed(OPM_CALCULATOR_MATURITY_DECIMAL_PLACES)));

const OPMCalculator: FC<OPMCalculatorProps> = ({
  opmCalculator,
  isDisabled,
  isLoading,
  isSaving,
  onInputsChange,
  exportTarget,
}) => {
  const { marketDate, measurementDate, opmCalculatorLinkedSources, registerApproachFormat } = useContext(ValuationContext);
  const { isExportMode } = useLayoutContext();
  const isExporting = Boolean(isExportMode && exportTarget);
  const [{ capTableList: capTableListFromStore } = {}] = (useStore() ?? [{}]) as unknown as [
    { capTableList?: CapTableOption[] | null },
  ];
  // `?? ` rather than a destructuring default: the store holds `null` (not `undefined`) once a
  // valuation unmounts, and a destructuring default would not cover that.
  const capTableList = capTableListFromStore ?? EMPTY_CAP_TABLE_LIST;
  // With a single cap table there is nothing to choose, so it is selected for the analyst.
  const defaultCapTableId = capTableList.length === 1 ? capTableList[0].id : undefined;
  const [getRiskFreeRateByDate] = useGetRiskFreeRate();

  // Cap Table Currency, per the design. Registered with the tertiary nav so its currency and
  // units selector drives this page; "Value per Share" opts out of the units scaler per cell.
  const [format, formatDispatch] = useFormat({ page: CAP_TABLE_CURRENCY_PAGE });

  const panelId = getStringValue(getOPMCalculatorPanelId(opmCalculator));

  useEffect(() => {
    registerApproachFormat?.(valuationTabId('opm_calculator', panelId), format, formatDispatch);
  }, [panelId, format, formatDispatch, registerApproachFormat]);

  // The date a calculator without one of its own is priced at: the valuation's market date,
  // falling back to the measurement date -- the same resolution the OPM input sets use.
  const fallbackOPMDateSource = marketDate || measurementDate?.date;
  const fallbackOPMDate = fallbackOPMDateSource ? moment(fallbackOPMDateSource).format(ISO_DATE_FORMAT) : undefined;

  // Set while a "Show OPM Calculations" link links this calculator to its source. The inputs the
  // source supplies are synced from it (`useSyncLinkedOPMCalculators`) and read-only here: they
  // are the source's numbers, edited on the source.
  const linkedSource = isExporting ? undefined : opmCalculatorLinkedSources?.[panelId];
  const prefilledFrom = linkedSource?.label || undefined;
  const lockedInputs = linkedSource?.lockedInputs;
  const { inputsFromUrl, setInputs: setUrlInputs } = useOPMCalculatorUrlState();
  const { tabId: activeTabId } = useApproachQueryParam();
  const isActiveTab = activeTabId === valuationTabId('opm_calculator', panelId);

  /**
   * Per-field precedence: a value present in the URL wins, otherwise the value saved on the
   * `OPMCalculator` row. That way a partial deep link still fills the rest from the model
   * instead of blanking the page. A locked input always reads the row: the sync writes the
   * source's number there, and a stale param must not hide it.
   */
  const inputs = useMemo<OPMCalculatorInputs>(() => {
    const saved = (key: keyof typeof opmCalculator) => {
      const value = opmCalculator[key];
      return value === null || value === undefined || value === '' ? undefined : Number(value);
    };
    const fromUrl = <K extends keyof OPMCalculatorInputs>(key: K) =>
      (lockedInputs?.includes(key) ? undefined : inputsFromUrl[key]);

    return {
      capTableId: fromUrl('capTableId') ?? saved('cap_table') ?? defaultCapTableId,
      equityValue: fromUrl('equityValue') ?? saved('equity_value'),
      riskFreeRate: fromUrl('riskFreeRate') ?? saved('risk_free_rate'),
      maturity: roundMaturity(fromUrl('maturity') ?? saved('maturity')),
      volatility: fromUrl('volatility') ?? saved('volatility'),
      // A date, so not through `saved`, which reads numbers.
      opmDate: fromUrl('opmDate') ?? toOPMDate(opmCalculator.opm_date),
    };
  }, [defaultCapTableId, inputsFromUrl, lockedInputs, opmCalculator]);

  // Keep the shareable URL on the synced numbers: rewrite any locked param that drifted from the
  // row. Only from the active tab -- the params belong to whichever calculator is open.
  useEffect(() => {
    if (!isActiveTab || !lockedInputs?.length) {
      return;
    }
    const drifted = lockedInputs.filter(
      key => inputsFromUrl[key] !== undefined && inputsFromUrl[key] !== inputs[key]
    );
    if (drifted.length) {
      setUrlInputs(Object.fromEntries(drifted.map(key => [key, inputs[key]])));
    }
  }, [inputs, inputsFromUrl, isActiveTab, lockedInputs, setUrlInputs]);

  // The date the ladder is priced at: the row's own date -- the one a "Show OPM Calculations"
  // link copied from its source (a Backsolve's backsolve date, a Future Exit's exit date) --
  // else the fallback. The FRED lookup and the results request both take it, so the rate and
  // the dividend accrual agree.
  const opmDate = inputs.opmDate || fallbackOPMDate;

  /**
   * Writes to both destinations: the URL (shareable) and the row (saved with the valuation).
   *
   * The sole-cap-table default is not written on its own -- that would mark the valuation as
   * having unsaved changes just for opening the tab -- so it rides along with the first edit.
   */
  const isCapTableDefaulted = Boolean(defaultCapTableId) && !inputsFromUrl.capTableId && !opmCalculator.cap_table;
  const commitInputs = useCallback(
    (partial: Partial<OPMCalculatorInputs>) => {
      const withCapTable =
        isCapTableDefaulted && !('capTableId' in partial) ? { capTableId: defaultCapTableId, ...partial } : partial;
      setUrlInputs(withCapTable);
      onInputsChange?.(panelId, withCapTable);
    },
    [defaultCapTableId, isCapTableDefaulted, onInputsChange, panelId, setUrlInputs]
  );

  // The rate is never typed. It is derived from maturity + the OPM date so the value
  // shown and the value the math uses come from the same (maturity, date) pair -- the same
  // approach the Backsolve OPM input table takes.
  const refreshRiskFreeRate = useCallback(
    // `onDate` for an OPM date edit, whose new date is not in `opmDate` until the next render.
    async (maturity?: number, onDate: string | undefined = opmDate) => {
      if (!maturity || maturity <= 0 || !onDate) {
        return;
      }
      const rate = (await getRiskFreeRateByDate(maturity, onDate)) as number | undefined;

      if (rate !== undefined && rate !== null) {
        commitInputs({ riskFreeRate: Number(Number(rate).toFixed(6)) });
      }
    },
    [commitInputs, getRiskFreeRateByDate, opmDate]
  );

  const { results, isError, isFetching, hasCompleteInputs } = useOPMCalculatorResults({ inputs, opmDate });

  const inputsSheet = useMemo(
    () => createInputsConfig({ panelId, inputs, fallbackOPMDate, capTableList, isDisabled, lockedInputs }),
    [panelId, inputs, fallbackOPMDate, capTableList, isDisabled, lockedInputs]
  );

  const resultSheets = useMemo(() => {
    if (!results) {
      return [];
    }
    // Same currency, units and exchange rate as the cells below the headers.
    const formatCurrency = (value: number) =>
      String(
        formatNumbers({ format: largeCurrencyFormat, currency: format?.currency, value } as unknown as Parameters<
          typeof formatNumbers
        >[0])
      );

    return [
      createLadderConfig({ panelId, inputs, results }),
      createAllocationConfig({ panelId, results, formatCurrency }),
      createSecurityConfig({ panelId, results }),
    ];
  }, [panelId, inputs, results, format]);

  // Derived, not state: holding the sheet list in state meant every recompute of either memo
  // wrote it back through an effect, so any dependency that changed identity per render turned
  // into an unbounded render loop. A memo has the same value with none of that feedback.
  const spreadsheets = useMemo<OPMCalculatorSpreadsheetConfig[]>(
    () => [inputsSheet, ...resultSheets],
    [inputsSheet, resultSheets]
  );

  const { onChange, workbook } = useWorkbook(
    spreadsheets as unknown as Parameters<typeof useWorkbook>[0]
  );

  /**
   * Every input edit builds new sheet configs, and a config has no `data` until the workbook
   * has evaluated it -- a render later. Rendering the fresh config meanwhile swapped the inputs
   * sheet for a skeleton and blanked the result tables, twice per edit (again when the tab is
   * rebuilt with the patched row), so the page looked like it reloaded. Keep showing the last
   * evaluated version of each sheet until its replacement has data.
   */
  const lastEvaluatedSheetsRef = useRef<Record<string, OPMCalculatorSpreadsheetConfig>>({});
  const toDisplayedSheet = (sheet: OPMCalculatorSpreadsheetConfig) => {
    const { name, data } = sheet as unknown as { name: string; data?: unknown[] };
    if (data) {
      lastEvaluatedSheetsRef.current[name] = sheet;
      return sheet;
    }
    return lastEvaluatedSheetsRef.current[name] ?? sheet;
  };
  const displayedInputsSheet = toDisplayedSheet(inputsSheet);

  // Gate on the render chain, not just the fetch: `keepPreviousData` can show stale results
  // while refetching, and the sheets only render once they are in the workbook.
  const exportStatus = useMemo<ExportSignalStatus>(() => {
    if (isError && !results) {
      return 'failed';
    }
    if (!hasCompleteInputs) {
      return 'missing';
    }
    const isRendered = spreadsheets.every(sheet => Boolean(workbook?.[(sheet as { name: string }).name]));
    if (!results || isFetching || resultSheets.length === 0 || !isRendered) {
      return 'loading';
    }
    return 'ready';
  }, [hasCompleteInputs, isError, isFetching, results, resultSheets.length, spreadsheets, workbook]);

  useExportSignal({
    target: getStringValue(exportTarget),
    status: exportStatus,
    enabled: isExporting,
  });

  /** Only the inputs sheet is editable; every result sheet is read-only by construction. */
  const handleInputsChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (cell: any, value: any) => {
      onChange?.(cell, value);

      switch (cell?.alias) {
        case CAP_TABLE_SELECTION_ALIAS:
          commitInputs({ capTableId: getNumberValue(value) || undefined });
          break;
        case EQUITY_VALUE_ALIAS:
          commitInputs({ equityValue: getNumberValue(value) });
          break;
        case MATURITY_ALIAS: {
          const maturity = roundMaturity(getNumberValue(value));
          commitInputs({ maturity });
          if (!lockedInputs?.includes('riskFreeRate')) {
            refreshRiskFreeRate(maturity);
          }
          break;
        }
        case VOLATILITY_ALIAS:
          commitInputs({ volatility: getNumberValue(value) });
          break;
        case OPM_DATE_ALIAS: {
          // The rate is read on this date, so a new date needs a new rate, as a new maturity does.
          const editedOPMDate = toOPMDate(value);
          commitInputs({ opmDate: editedOPMDate });
          // A synced rate is the source's, not this date's lookup.
          if (!lockedInputs?.includes('riskFreeRate')) {
            refreshRiskFreeRate(inputs.maturity, editedOPMDate || fallbackOPMDate);
          }
          break;
        }
        default:
          break;
      }
    },
    [commitInputs, fallbackOPMDate, inputs.maturity, lockedInputs, onChange, refreshRiskFreeRate]
  );

  const sheetProps = (sheet: never, extra: object = {}) => ({
    ...(sheet as object),
    sheet,
    workbook,
    onChange,
    page: CAP_TABLE_CURRENCY_PAGE,
    format,
    data: (sheet as { data?: unknown[] })?.data ?? [],
    isLoading,
    isSaving,
    ...extra,
  });

  return (
    <CustomContainer
      data-testid={VALUATION_APPROACHES_TEST_IDS.opmCalculatorPanel}
      data-ref={isExporting ? exportTarget : undefined}
      style={isExporting ? EXPORT_MODE_STYLES : undefined}>
      <Grid container spacing={4}>
        {/*
          Full width, like every other sheet on the page. The inputs sheet is naturally narrow
          -- one titles column plus one value column -- so it does not need a narrower grid
          item, and constraining it clipped the value column behind a horizontal scrollbar.
        */}
        {prefilledFrom && (
          <Grid size={{ xs: 12 }}>
            <Typography
              color="textSecondary"
              data-testid={OPM_CALCULATOR_PREFILLED_FROM_TEST_ID}
              variant="caption">
              {getOPMCalculatorPrefilledFromCaption(prefilledFrom)}
            </Typography>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          {(displayedInputsSheet as { data?: unknown[] })?.data ? (
            <ScalarSpreadsheet {...sheetProps(displayedInputsSheet as never, { onChange: handleInputsChange })} />
          ) : (
            <GridSkeleton />
          )}
        </Grid>

        {/*
          Inline rather than a MessageBox: a centred 500px card floats away from the tables,
          which are all left-aligned and left-anchored.
        */}
        {!hasCompleteInputs && !results && (
          <Grid size={{ xs: 12 }}>
            <InlineMessageBox showIcon text={OPM_CALCULATOR_PLACEHOLDER_TAGLINE} />
          </Grid>
        )}

        {isError && !results && (
          <Grid size={{ xs: 12 }}>
            <InlineMessageBox showIcon text={OPM_CALCULATOR_ERROR_TITLE} />
          </Grid>
        )}

        {/*
          Results stay mounted while the analyst edits an input (the query keeps previous data),
          so the page does not blank out between keystrokes.
        */}
        {resultSheets.map(sheet => (
          <Grid key={(sheet as { name: string }).name} size={{ xs: 12 }}>
            <ScalarSpreadsheet {...sheetProps(toDisplayedSheet(sheet) as never)} />
          </Grid>
        ))}
      </Grid>
    </CustomContainer>
  );
};

export default OPMCalculator;
